import React, { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import {
  XCircle,
  AlertTriangle,
  User,
  Award,
  Loader2
} from 'lucide-react';

const MarkFailedDialog = ({ open, onOpenChange, delegate, booking, onSuccess }) => {
  const { userProfile } = useAuth();
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const handleDialogChange = (isOpen) => {
    if (!isOpen) {
      setNotes('');
      setSaving(false);
    }
    onOpenChange(isOpen);
  };

  const handleConfirmFailed = async () => {
    setSaving(true);
    const loadingToast = toast.loading('Marking delegate as failed...');

    try {
      const userId = delegate.space_member?.user_id;

      // 1. Update booking_delegates with failed status
      const { error: updateError } = await supabase
        .from('booking_delegates')
        .update({
          attendance_status: 'attended', // They attended but failed
          completion_status: 'failed',
          internal_notes: notes || null,
          updated_date: new Date().toISOString(),
          updated_by: userProfile?.id
        })
        .eq('id', delegate.id);

      if (updateError) throw updateError;

      // 2. Update user_qualifications to reflect failure
      // Set renewal_status back to 'quoting' or 'renewal_needed' so they need to retake
      if (booking.course_id && userId) {
        const { error: qualError } = await supabase
          .from('user_qualifications')
          .update({
            status: 'expired', // Mark as expired since they failed
            renewal_status: 'renewal_needed', // Needs to be renewed/retaken
            notes: notes ? `Failed training on ${new Date().toLocaleDateString('en-GB')}: ${notes}` : `Failed training on ${new Date().toLocaleDateString('en-GB')}`,
            updated_date: new Date().toISOString()
          })
          .eq('user_id', userId)
          .eq('course_link_id', booking.course_id.toString());

        if (qualError) {
          console.warn('Failed to update user_qualifications:', qualError);
        }
      }

      toast.success('Delegate marked as failed', { id: loadingToast });

      // Close dialog and refresh
      handleDialogChange(false);
      if (onSuccess) onSuccess();

    } catch (error) {
      console.error('Error marking delegate as failed:', error);
      toast.error('Failed to update delegate status', { id: loadingToast });
    } finally {
      setSaving(false);
    }
  };

  const profile = delegate?.space_member?.user_profiles;

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <XCircle className="h-5 w-5" />
            Mark Delegate as Failed
          </DialogTitle>
          <DialogDescription>
            Confirm that {profile?.display_name || 'this delegate'} did not pass the training
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Warning */}
          <div className="bg-red-50 rounded-lg p-4 border border-red-200">
            <div className="flex gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-900 mb-1">
                  This action will update the delegate's records
                </p>
                <p className="text-xs text-red-700">
                  The delegate will need to retake this training to become qualified.
                </p>
              </div>
            </div>
          </div>

          {/* Delegate Info */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <p className="font-medium text-sm">{profile?.display_name}</p>
                <p className="text-xs text-gray-600">{profile?.email}</p>
              </div>
            </div>
            <Separator className="my-3" />
            <div className="flex items-center gap-2 text-sm">
              <Award className="h-4 w-4 text-gray-600" />
              <span className="font-medium">{booking?.course_name}</span>
            </div>
          </div>

          {/* Notes Field */}
          <div className="space-y-2">
            <Label htmlFor="failure-notes" className="text-sm font-medium">
              Reason for Failure (Optional)
            </Label>
            <Textarea
              id="failure-notes"
              placeholder="E.g., Did not pass assessment, incomplete attendance, etc."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="resize-none"
              disabled={saving}
            />
            <p className="text-xs text-gray-500">
              This note will be saved internally and added to the user's qualification record.
            </p>
          </div>

          {/* What will happen */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <p className="text-xs font-medium text-blue-900 mb-2">What will happen:</p>
            <ul className="text-xs text-blue-800 space-y-1">
              <li>• Delegate attendance marked as <strong>Attended</strong></li>
              <li>• Completion status set to <strong>Failed</strong></li>
              <li>• User qualification status set to <strong>Expired</strong></li>
              <li>• Renewal status set to <strong>Renewal Needed</strong></li>
              <li>• Delegate will need to retake this training</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleDialogChange(false)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirmFailed}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 mr-2" />
                Confirm Failed
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarkFailedDialog;
